import * as Sentry from '@sentry/react';
import {
  buildFactorChoicePayload,
  MFA_FACTOR_CHOICE_CATEGORY,
  type MfaFactorChoiceEvent,
  type MfaFactorTier,
  type MfaFactorFailureReason,
  type MfaGateBlockedReason,
} from '@usacsmideo/types';

/**
 * Factor-choice telemetry — CU-86ak54cyz, the IRRECOVERABLE half.
 *
 * ── ⚠️ WHY THIS CANNOT WAIT FOR THE CUTOVER ───────────────────────────────
 *
 * Final state is reconstructable from Firestore whenever we like. ATTEMPT AND
 * FAILURE ARE NOT. A patient who tries a passkey, hits an error and falls back
 * to SMS ends up holding an SMS factor — byte-for-byte identical to a patient
 * who simply chose SMS. Those are OPPOSITE signals for the retirement decision,
 * and only the moment distinguishes them.
 *
 * The cutover is one-shot: every patient passes this gate once, when their
 * session is revoked. Instrumentation that lands afterwards observes an empty
 * funnel.
 *
 * ── ⚠️ `captureMessage`, NOT `addBreadcrumb` ──────────────────────────────
 *
 * A breadcrumb only surfaces attached to a captured event. The funnel's whole
 * value is in the runs where NOTHING throws — a patient who quietly picks SMS
 * raises no error, and a breadcrumb of that is written to nowhere.
 *
 * ── ⚠️ VOLUME IS BOUNDED BY EVENT SHAPE, NOT BY SAMPLING ──────────────────
 *
 * These are `level: 'info'` messages, and there are at most a handful per
 * patient per cutover: one `gate-shown` per gate mount, and one per ATTEMPT the
 * patient actually makes. It is not per-render — the gate-shown emitter
 * deduplicates on the offer set (see `emitGateShown`), so React re-renders and
 * the twin `useMfaRouting` call sites cannot multiply it.
 *
 * ⚠️ DELIBERATELY SEPARATE FROM THE CU-86ajxvg7y GATE TRAIL. Different
 * category, different message prefix. That task answers "why was THIS patient
 * gated?"; this one answers "what did the POPULATION choose?". Merging them
 * would put support-diagnosis noise inside the adoption denominator.
 *
 * ── PHI ───────────────────────────────────────────────────────────────────
 *
 * ⚠️ THE PAYLOAD CANNOT CARRY FREE TEXT — every value is a boolean or a member
 * of a closed union, enforced by `buildFactorChoicePayload`'s type and pinned by
 * a test in `@usacsmideo/types`. No phone number, no email, no error prose. The
 * subject uid rides Sentry's user scope and is never a field.
 *
 * ⚠️ THE USER SCOPE IS **NOT** SCRUBBED — this comment said "already-scrubbed"
 * until 2026-09-03. Sentry's default scrubbers match FIELD NAMES (`password`,
 * `token`, `secret`, …) and `user.id` is not among them, so the uid is stored
 * in full. That is CORRECT under the uid-ceiling policy — the uid is the
 * maximum identifier permitted, never resolved to a name, email or phone — but
 * do not reason from "it's scrubbed" when deciding what else may sit nearby.
 * A false mechanism claim on an auth path is how the next field gets waved
 * through. See `CLAUDE/docs/guides/sentry-phi-determinations.md`.
 */

/** Message prefix. ⚠️ Changing it breaks any saved Sentry search — update both. */
const MESSAGE_PREFIX = 'mfa factor choice';

/**
 * Emit one factor-choice observation.
 *
 * ⚠️ NEVER THROWS. Telemetry that can break the ceremony it observes is worse
 * than no telemetry — the patient's route to their records outranks our data,
 * always. Same contract as the mobile diagnostic buffer.
 */
function emit(event: MfaFactorChoiceEvent): void {
  try {
    Sentry.captureMessage(`${MESSAGE_PREFIX}: ${event.kind}`, {
      level: 'info',
      tags: {
        feature: 'mfa',
        // ⚠️ The tag is what makes this queryable as a funnel rather than as
        // prose. Sentry groups on message; the aggregate lives in the tags.
        signal: MFA_FACTOR_CHOICE_CATEGORY,
        // ⚠️ Names the platform so the funnel can be split. Mirrors mobile's
        // `platform: "mobile"` — passkey UX differs materially between the web
        // and iOS, so a blended number would hide a platform-specific failure.
        // Present on BOTH clients or the split is unreadable on half the
        // population.
        platform: 'web',
        // ⚠️ PROMOTED FROM `extra` (CU-86akc9k1g). `kind` was ONLY interpolated
        // into the message and used as the fingerprint — neither is queryable.
        // Without it as a tag, a rollup must identify `gate-shown` by MESSAGE
        // SUBSTRING, so changing `MESSAGE_PREFIX` above would silently zero the
        // funnel's DENOMINATOR rather than fail. It stays in `extra` too:
        // promotion is ADDITIVE, the payload remains the record.
        //
        // ⚠️ A CLOSED SET OF THREE: `gate-shown` | `factor-attempt` |
        // `gate-blocked`, and it is the `MfaFactorChoiceEvent` discriminant, so
        // the union bounds it.
        //
        // ⚠️ THIS COMMENT SAID "EXACTLY TWO" AND WAS TRUE UNTIL types@1.54.0
        // (CU-86akjgbyw). The branch below was written as an `else`, so adding a
        // third member silently routed `gate-blocked` into the `gate-shown`
        // arm — `tsc` caught it only because that arm reads a field the new
        // member does not have. Had the two shared a field name, this would
        // have compiled and mis-tagged every blocked render. Hence explicit
        // per-kind branches below, not an else.
        kind: event.kind,
        ...(event.kind === 'factor-attempt'
          ? {
              tier: event.tier,
              outcome: event.outcome,
              // ⚠️ TAGGED ONLY ON FAILURE, and only when a reason is present.
              // `failureReason` also stays in `extra` (see below) — tags are for
              // GROUPING, the payload is the record, and dropping it from the
              // payload would be a lossy trade.
              //
              // ⚠️ NO `"(none)"` TAG ON SUCCESS. `extra` uses that sentinel
              // because an absent key and a valueless key are the same thing in
              // a Sentry SEARCH. A TAG is different: it becomes a grouping
              // dimension, and a dominant null bucket makes the failure
              // breakdown unreadable — the successes would swamp every chart.
              // Absent-on-success is what makes "why did passkeys fail?"
              // answerable by grouping on this tag alone.
              ...(event.outcome === 'failed' && event.failureReason
                ? { failureReason: event.failureReason }
                : {}),
            }
          : event.kind === 'gate-blocked'
          ? {
              // ⚠️ THE BLOCKED STATE'S GROUPING DIMENSIONS — CU-86akjgbyw.
              //
              // `reason` answers "why was nothing offered"; `passkeyAtRisk`
              // sizes the cohort standing at the H4 cliff (patients holding a
              // passkey that verifying their email would destroy). Both are
              // closed unions/booleans, so neither can carry prose.
              //
              // ⚠️ Stringified for the same reason as `credentialsSettled` —
              // Sentry tag values are strings. `extra` keeps the real boolean.
              reason: event.reason,
              passkeyAtRisk: String(event.passkeyAtRisk),
            }
          : {
              // ⚠️ PROMOTED FROM `extra` (CU-86akc9k1g), and `gate-shown`-only
              // because that is the only kind that HAS one. The gate is
              // optimistic while the credential list is in flight, so an
              // unsettled render can offer `passkey-use` to a patient who holds
              // nothing. Read without this tag, such a render is
              // INDISTINGUISHABLE FROM A GENUINE PASSKEY FAILURE, which
              // silently corrupts the failure rate the funnel exists to report.
              //
              // ⚠️ Stringified because Sentry tag values are strings — a boolean
              // arrives as `"true"`/`"false"` anyway, and doing it here keeps the
              // closed set explicit rather than SDK-dependent. `extra` keeps the
              // real boolean; this is additive.
              //
              // ⚠️ NOT the `"(none)"` case. This is a genuine two-value field
              // present on EVERY `gate-shown`, so it creates no dominant null
              // bucket — unlike `failureReason`, which is deliberately absent on
              // success for exactly that reason.
              credentialsSettled: String(event.credentialsSettled),
            }),
      },
      extra: buildFactorChoicePayload(event),
      /**
       * ── ⚠️ THE FINGERPRINT IS WHAT MAKES THE ARCHIVE DURABLE ──────────────
       *
       * ⚠️ WITHOUT THIS, SENTRY GROUPS THESE BY STACKTRACE, NOT BY MESSAGE.
       * MEASURED 2026-09-01: the same three logical events produced SIX issues
       * — A0/A1/A2 from the portal (message-titled) and A3/A4/A5 from mobile
       * (titled `emit`/`recordGateDecision`, from minified `main.jsbundle`
       * frames). Same events, different stacks, separate issues.
       *
       * ⚠️ AND IT IS NOT A ONE-OFF SPLIT. Frames move whenever the bundle
       * changes, so EVERY new build mints FRESH issues. The production EAS
       * build would do exactly that mid-cutover, which is why archiving the
       * six that exist today would be a snapshot rather than a fix.
       *
       * Pinning the group to the EVENT KIND collapses every platform and every
       * future bundle into a small FIXED set, which is the property that lets
       * "archive Forever" actually hold. Same technique, same reason as
       * `useFirestoreListener`'s
       * `fingerprint: ['firestore-listener', 'first-attempt', error.code]`,
       * which is why MIDEO-UNIFIED-9X stayed ONE issue while this fragmented
       * into six.
       *
       * ⚠️ ONE GROUP PER KIND, DELIBERATELY NOT ONE GROUP TOTAL. `gate-shown`
       * is the funnel's DENOMINATOR and `factor-attempt` its NUMERATOR; merging
       * them would make the archive set smaller but destroy the ability to read
       * either one alone. Three questions, three groups.
       *
       * ⚠️ DELIBERATELY EXCLUDES tier/outcome/failureReason. Those are TAGS —
       * they group in Discover WITHOUT splitting the issue. Putting them here
       * would re-fragment the very thing this exists to hold together, and the
       * archive set would grow with every new union member.
       *
       * ⚠️ NO PLATFORM IN THE KEY, ON PURPOSE. Portal and mobile are one funnel;
       * `platform` is already a tag for splitting when that is the question.
       */
      fingerprint: ['mfa-telemetry', event.kind],
    });
  } catch {
    // Swallowed on purpose. See the contract above.
  }
}

/**
 * ⚠️ MODULE-LEVEL, NOT PER-COMPONENT — and that is the point.
 *
 * `useMfaRouting` is called TWICE per gated patient view (the route and the
 * gate), and the gate component re-renders on every credential-list settle.
 * Per-instance React state would deduplicate neither. A module-level set is
 * shared across both call sites and survives re-renders, so one gate
 * presentation yields one event.
 *
 * Not cleared on sign-out: the key includes the offer set, the page reloads on
 * a real session change, and over-suppressing a duplicate is harmless where
 * double-counting the denominator is not.
 */
const seenGateOffers = new Set<string>();

/** Test seam. ⚠️ Never called from app code. */
export function __resetFactorChoiceTelemetryForTest(): void {
  seenGateOffers.clear();
}

/**
 * The gate was SHOWN — the funnel's DENOMINATOR.
 *
 * Without it, "12 SMS enrollments" cannot be read as a rate, and a tier that was
 * never OFFERED is indistinguishable from one offered and refused — a
 * presentation bug versus a patient preference.
 *
 * ⚠️ `credentialsSettled` MATTERS. The gate is optimistic while the credential
 * list is in flight (`credentials === null` still offers "use"), so an unsettled
 * render can offer `passkey-use` to a patient who holds nothing. A funnel
 * ignoring this would score those as passkey FAILURES rather than as a known
 * consequence of an unresolved read.
 */
export function emitGateShown(
  offeredTiers: MfaFactorTier[],
  credentialsSettled: boolean,
): void {
  const key = `${[...offeredTiers].sort().join(',')}|${credentialsSettled}`;
  if (seenGateOffers.has(key)) return;
  seenGateOffers.add(key);
  emit({ kind: 'gate-shown', offeredTiers, credentialsSettled });
}

/**
 * The gate rendered and offered NOTHING — CU-86akjgbyw.
 *
 * ⚠️ A DISTINCT EVENT KIND, DELIBERATELY NOT `emitGateShown([], …)`. An empty
 * offer set reads as "gate shown, nothing taken", which puts every blocked
 * patient into the adoption DENOMINATOR as someone who was offered factors and
 * refused them all. That is the opposite conclusion from the truth, and it
 * would silently depress the very rate this funnel exists to report.
 *
 * ⚠️ DEDUPLICATED ON THE FULL STATE, not just the reason. A patient whose
 * credential list settles after the first render legitimately changes
 * `passkeyAtRisk` — that transition is real and worth one event each, because
 * it is how we learn the at-risk count was initially a guess. Re-renders that
 * change nothing still collapse to one.
 *
 * ⚠️ SHARES THE `seenGateOffers` SET WITH `emitGateShown`, on purpose. The two
 * are mutually exclusive presentations of the same mount, and one set means a
 * gate that flips from blocked to shown (the patient verifies mid-session and
 * presses recheck) emits one of each rather than re-emitting either.
 */
export function emitGateBlocked(
  reason: MfaGateBlockedReason,
  passkeyAtRisk: boolean,
): void {
  const key = `blocked|${reason}|${passkeyAtRisk}`;
  if (seenGateOffers.has(key)) return;
  seenGateOffers.add(key);
  emit({ kind: 'gate-blocked', reason, passkeyAtRisk });
}

/**
 * A tier was ATTEMPTED and RESOLVED.
 *
 * ⚠️ CALL THIS ON RESOLUTION, NEVER ON CLICK. The outcome is the entire signal:
 * an attempt event without one makes a failure look like a success in every
 * aggregate, because the natural query ("count attempts by tier") counts both
 * identically — and attempt-vs-failure is precisely what cannot be recovered
 * afterwards.
 *
 * ⚠️ NOT DEDUPLICATED, deliberately. A patient who tries a passkey three times
 * before falling back to SMS has told us something real, and collapsing those
 * retries would hide the friction the funnel exists to measure.
 */
export function emitFactorAttempt(
  tier: MfaFactorTier,
  outcome: 'succeeded' | 'failed',
  failureReason?: MfaFactorFailureReason,
): void {
  emit({ kind: 'factor-attempt', tier, outcome, failureReason });
}
